import type { PlayerCommand } from '@nine/shared';
import type { Input } from './input';
import { aimBegin, aimEnd, aimingByPoint, aimNudge } from './groundAim';
import type { Hud } from './ui/hud';

/** Buttons of the browser's "standard" gamepad mapping (Xbox names; PlayStation in brackets). */
const Btn = {
  A: 0, // ✕
  B: 1, // ○
  X: 2, // □
  Y: 3, // △
  LB: 4, // L1
  RB: 5, // R1
  LT: 6, // L2
  RT: 7, // R2
  Back: 8, // Share / Create
  Start: 9, // Options
  L3: 10,
  R3: 11,
  Up: 12,
  Down: 13,
  Left: 14,
  Right: 15,
} as const;
const FACE = [Btn.A, Btn.B, Btn.X, Btn.Y] as const;
/** Stick travel below this counts as centred. */
const DEADZONE = 0.25;
/** The right stick acts like a key: held past PRESS, let go once back under RELEASE. */
const FLICK_PRESS = 0.6;
const FLICK_RELEASE = 0.3;
/** Headings snap to this many directions, so a resting thumb does not resend a move every frame. */
const HEADINGS = 16;

/**
 * Plays the game with a gamepad. Browsers only update pads when asked, so {@link poll} runs once
 * per frame from the main loop.
 *
 * Left stick / d-pad walk. A jump, B dash, X 攻 basic attack, Y talk. LT held 守 guard, RT held 奔 run.
 * Holding LB turns A B X Y into moves 1–4, holding RB into moves 5–8. Right stick: sideways turns
 * the camera, up/down zooms. L3 picks the nearest foe, R3 sheathes. Start = Esc, Back = map.
 * A ground move (ท่าเลือกพื้นที่) is held: the reticle starts at the foe's feet, the right stick moves it, letting go
 * casts, B cancels.
 */
export class GamepadInput {
  private prev: boolean[] = [];
  private flickY = 0;
  /** Right stick held sideways (turning the camera), quantised so a resting thumb sends nothing. */
  private turnX = 0;
  private heading: [number, number] = [0, 0];
  private block = false;
  private sprint = false;
  private flying = false;
  private active = false;
  /** The face button holding a ground move's aim (groundAim.ts), or -1; when the aim last moved. */
  private aimBtn = -1;
  private aimAt = 0;

  constructor(
    private readonly input: Input,
    private readonly hud: Hud,
    private readonly send: (c: PlayerCommand) => void,
    private readonly onGesture: () => void,
    private readonly onMap: () => void,
  ) {
    addEventListener('gamepadconnected', () => this.hud.toast('เชื่อมต่อจอยแล้ว · ดูปุ่มได้ในวิธีเล่น (H)'));
    addEventListener('gamepaddisconnected', () => {
      if (!this.pad()) this.release();
    });
  }

  /** The first connected pad, if any. */
  private pad(): Gamepad | null {
    if (!navigator.getGamepads) return null;
    for (const gp of navigator.getGamepads()) if (gp?.connected) return gp;
    return null;
  }

  poll(): void {
    const gp = this.pad();
    if (!gp) return;
    const down = gp.buttons.map((b) => b.pressed);
    const pressed = (b: number) => !!down[b] && !this.prev[b];
    const released = (b: number) => !down[b] && !!this.prev[b];
    this.prev = down;

    // Start and the menus work before the game starts (and with windows open).
    if (pressed(Btn.Start)) this.input.escape();
    if (!this.input.enabled) return;
    if (down.some(Boolean)) this.onGesture();
    if (pressed(Btn.Back)) this.onMap();

    this.walk(gp, down);
    // Aiming a ground move (groundAim.ts): the right stick moves the reticle, letting go of its button casts, B cancels.
    if (this.aimBtn >= 0 && !aimingByPoint()) this.aimBtn = -1;
    if (this.aimBtn >= 0) {
      const now = performance.now();
      aimNudge(gp.axes[2] ?? 0, gp.axes[3] ?? 0, Math.min(0.05, (now - this.aimAt) / 1000));
      this.aimAt = now;
      if (pressed(Btn.B) || !down[this.aimBtn]) {
        aimEnd(!pressed(Btn.B));
        this.aimBtn = -1;
      }
      this.active = true;
      return;
    }
    this.camera(gp);

    // Held actions: send only when they change.
    const block = !!down[Btn.LT];
    if (block !== this.block) this.input.setPadBlock((this.block = block));
    const sprint = !!down[Btn.RT];
    if (sprint !== this.sprint) this.send({ t: 'sprint', on: (this.sprint = sprint) });

    const shift = down[Btn.LB] ? 0 : down[Btn.RB] ? 4 : -1;
    if (shift >= 0) {
      FACE.forEach((b, i) => {
        if (!pressed(b) || this.aimBtn >= 0) return;
        if (aimBegin(shift + i)) {
          this.aimBtn = b;
          this.aimAt = performance.now();
          if (this.turnX) this.input.setPadTurn((this.turnX = 0));
        } else this.input.useSlot(shift + i);
      });
    } else {
      if (pressed(Btn.A)) {
        this.input.jumpOrClimb();
        this.flying = true;
      }
      if (pressed(Btn.B)) {
        this.input.dashOrDive();
        this.flying = true;
      }
      if (pressed(Btn.X)) this.hud.basicAttack();
      if (pressed(Btn.Y)) this.input.talkNearest();
    }
    // Letting go of A / B stops climbing or diving on a flying mount (as with Space / Q).
    if (this.flying && (released(Btn.A) || released(Btn.B)) && !down[Btn.A] && !down[Btn.B]) {
      this.flying = false;
      this.send({ t: 'fly', dir: 0 });
    }
    if (pressed(Btn.L3)) this.send({ t: 'targetNearest' });
    if (pressed(Btn.R3)) this.send({ t: 'sheathe' });
    this.active = true;
  }

  private walk(gp: Gamepad, down: boolean[]): void {
    let sx = gp.axes[0] ?? 0;
    let sy = gp.axes[1] ?? 0;
    if (Math.hypot(sx, sy) < DEADZONE) sx = sy = 0;
    // The d-pad walks too, when the stick is centred.
    if (!sx && !sy) {
      sx = (down[Btn.Right] ? 1 : 0) - (down[Btn.Left] ? 1 : 0);
      sy = (down[Btn.Down] ? 1 : 0) - (down[Btn.Up] ? 1 : 0);
    }
    let heading: [number, number] = [0, 0];
    if (sx || sy) {
      const step = (Math.PI * 2) / HEADINGS;
      const a = Math.round(Math.atan2(sy, sx) / step) * step;
      heading = [Math.round(Math.cos(a) * 1000) / 1000, Math.round(Math.sin(a) * 1000) / 1000];
    }
    if (heading[0] === this.heading[0] && heading[1] === this.heading[1]) return;
    this.heading = heading;
    this.input.setPadDir(...heading);
  }

  private camera(gp: Gamepad): void {
    // Sideways turns the camera, as fast as the stick is pushed; let go, it settles on an angle.
    const rx = gp.axes[2] ?? 0;
    const turn =
      Math.abs(rx) < DEADZONE ? 0 : Math.round(((Math.abs(rx) - DEADZONE) / (1 - DEADZONE)) * 4) / 4;
    const turnX = Math.sign(rx) * Math.max(turn, Math.abs(rx) < DEADZONE ? 0 : 0.25);
    if (turnX !== this.turnX) this.input.setPadTurn((this.turnX = turnX));
    const ry = gp.axes[3] ?? 0;
    // Held up or down, the zoom goes on steadily; a flick zooms a step.
    if (!this.flickY && Math.abs(ry) > FLICK_PRESS) {
      this.flickY = Math.sign(ry);
      this.input.setPadZoom(-this.flickY);
    } else if (this.flickY && Math.abs(ry) < FLICK_RELEASE) {
      this.flickY = 0;
      this.input.setPadZoom(0);
    }
  }

  /** The pad went away: let go of everything it was holding. */
  private release(): void {
    this.prev = [];
    if (this.flickY) this.input.setPadZoom((this.flickY = 0));
    if (this.turnX) this.input.setPadTurn((this.turnX = 0));
    if (!this.active) return;
    this.active = false;
    this.heading = [0, 0];
    this.input.setPadDir(0, 0);
    if (this.block) this.input.setPadBlock((this.block = false));
    if (this.sprint) this.send({ t: 'sprint', on: (this.sprint = false) });
    if (this.flying) {
      this.flying = false;
      this.send({ t: 'fly', dir: 0 });
    }
  }
}
